/**
 * <TutorSessionRoster> — les élèves d'UNE séance du masteur, un par ligne : son nom, sa classe, ses
 * matières, et ce qui le distingue ce jour-là (molecule).
 *
 * L'ancien `StudentsListStringForOccurrence` (« Jules M. 4ᵉ, Léa B. 4ᵉ »), une ligne par élève
 * parce que la matière est la SIENNE (Wajih, 29/10 : dans un cours partagé, chaque élève vient avec
 * la sienne) : la pile de ses matières, puis « Jules M. · 4ᵉ · Maths » (`tutorStudentLabel`) — la
 * ligne d'une séance (`<SessionDetailsLine>`), à l'échelle d'un élève. Chaque élève porte sa marque
 * du jour —
 *  - ajouté pour cette date seulement (un rattrapage) : l'horloge, le nom en gras ;
 *  - retiré pour cette date : l'horloge, le nom barré, la pile estompée ;
 *  - absence ou retard signalés : « (abs.) » en rouge, « (retard) » en orange ;
 *  - paiement en attente : le cadenas, « (impayé·e) » ;
 *  - pas encore inscrit (une réservation) : le livre, le nom en gris — orange pour une
 *    réservation ponctuelle, bleu pour une régulière.
 * Une marque se cumule : un élève retiré ET absent a son nom barré, et « (abs.) » à côté, lisible.
 * Personne : « (aucun élève) ». Les élèves arrivent tels que l'adaptateur les a dédoublonnés et
 * ordonnés (ajoutés, retirés, puis les autres, puis les réservations — l'ordre du legacy).
 *
 * C'est la ligne de la séance du masteur, sous son type de cours (`tutorSessionAnatomy`) : sur son
 * accueil comme dans sa semaine, il lit qui vient À CETTE date, par son nom. Pas de visages : un
 * prénom se lit, une initiale dans un rond se devine (Wajih, 29/10).
 *
 * `dimmed` l'estompe avec sa séance (terminée, absence signalée — `isSessionDimmed`) : les noms
 * passent à l'encre secondaire, les piles s'estompent ; les marques du jour gardent leur couleur,
 * comme le badge de la séance.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { DETAILS_LINE_BOX } from "../SessionDetailsLine/SessionDetailsLine"
import { SubjectBadges } from "../SubjectBadges"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { tutorStudentLabel } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import {
    type UITutorSessionStudent,
    type UITutorSessionStudentMark,
} from "../../../ports/primary/ui/types/tutorCoquilleAccueil"

const TEXTS = {
    empty: "(aucun élève)",
    absent: "(abs.)",
    late: "(retard)",
    paymentBlocked: "(impayé·e)",
}

/** Ce qu'un lecteur d'écran entend pour chaque marque — la forme n'a pas de son. */
const SPOKEN: Record<UITutorSessionStudentMark, string> = {
    added: "ajouté pour cette séance",
    removed: "retiré de cette séance",
    absent: "absence signalée",
    late: "retard signalé",
    payment_blocked: "paiement en attente",
    reservation: "réservation",
    reservation_adhoc: "réservation ponctuelle",
}

/** La marque de l'élève qui dit sa couleur, dans l'ordre du legacy : impayé, puis retard, puis absence. */
function suffixOf(marks: UITutorSessionStudentMark[]): { text: string; color: SemanticColorKey } | null {
    if (marks.includes("payment_blocked")) return { text: TEXTS.paymentBlocked, color: "text-warning" }
    if (marks.includes("late")) return { text: TEXTS.late, color: "text-warning" }
    if (marks.includes("absent")) return { text: TEXTS.absent, color: "text-danger" }
    return null
}

function glyphsOf(marks: UITutorSessionStudentMark[]): { name: IconName; color: SemanticColorKey }[] {
    const glyphs: { name: IconName; color: SemanticColorKey }[] = []
    if (marks.includes("added") || marks.includes("removed")) glyphs.push({ name: "clock", color: "primary" })
    if (marks.includes("payment_blocked")) glyphs.push({ name: "lock", color: "text-warning" })
    if (marks.includes("reservation_adhoc")) glyphs.push({ name: "book", color: "text-warning" })
    else if (marks.includes("reservation")) glyphs.push({ name: "book", color: "info" })
    return glyphs
}

export type TutorSessionRosterProps = {
    students: UITutorSessionStudent[]
    /** La séance est terminée, ou l'absence y est signalée : la liste s'estompe avec elle. Défaut `false`. */
    dimmed?: boolean
    testID?: string
}

const NO_MARKS: UITutorSessionStudentMark[] = []

export function TutorSessionRoster(props: TutorSessionRosterProps) {
    const { students, dimmed = false, testID } = props

    if (students.length === 0) {
        return (
            <Text size="text-sm" color="text-muted" style={[styles.line, styles.reservation]} testID={testID}>
                {TEXTS.empty}
            </Text>
        )
    }

    return (
        <Stack gap="1" testID={testID}>
            {students.map((student) => {
                const marks = student.marks ?? NO_MARKS
                const reserved = marks.includes("reservation") || marks.includes("reservation_adhoc")
                const removed = marks.includes("removed")
                const muted = reserved || removed || dimmed
                const suffix = suffixOf(marks)
                const label = tutorStudentLabel(student)
                const spoken = [label, ...marks.map((mark) => SPOKEN[mark])].join(", ")
                const glyphs = glyphsOf(marks)
                return (
                    <View key={student.id} accessible accessibilityLabel={spoken}>
                        <Row gap="2" align="start">
                            <SubjectBadges
                                subjects={student.subjects ?? []}
                                showLabel={false}
                                dimmed={removed || dimmed}
                            />
                            {glyphs.length > 0 && (
                                <Row gap="1" align="center" style={styles.glyphs}>
                                    {glyphs.map((glyph) => (
                                        <Icon key={glyph.name} name={glyph.name} size={16} color={glyph.color} />
                                    ))}
                                </Row>
                            )}
                            {/* Le nom porte ce qui le barre ou l'incline ; la marque, à côté, n'en hérite pas. */}
                            <Text size="text-sm" style={[styles.line, styles.label]}>
                                <Text
                                    size="text-sm"
                                    weight={marks.includes("added") ? "bold" : "regular"}
                                    color={muted ? "text-muted" : (suffix?.color ?? "text-primary")}
                                    style={[
                                        removed ? styles.removed : undefined,
                                        reserved ? styles.reservation : undefined,
                                    ]}
                                >
                                    {label}
                                </Text>
                                {suffix != null && (
                                    <Text size="text-xs" weight="bold" color={suffix.color}>
                                        {` ${suffix.text}`}
                                    </Text>
                                )}
                            </Text>
                        </Row>
                    </View>
                )
            })}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    /** La hauteur d'une chip de matière : la pile se cale sur la ligne, avec ou sans elle. */
    line: {
        lineHeight: DETAILS_LINE_BOX.height,
    },
    label: {
        flex: 1,
        flexShrink: 1,
    },
    glyphs: {
        height: DETAILS_LINE_BOX.height,
    },
    removed: {
        textDecorationLine: "line-through",
    },
    reservation: {
        fontStyle: "italic",
    },
}))
